import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import { filter, takeUntil, tap, switchMap } from 'rxjs/operators';
import { YaEvent, YaReadyEvent } from 'angular8-yandex-maps';
import { ILabomatesResponse, IPlacemark } from '../../lab-pass-types';
import { LabPassRepositoryService } from '../../services/lab-pass.repository.service';

import {
  placemarkCenterOptions,
  placemarkCenterSelectedOptions,
  clustererOptions,
  iconContentLayout,
  optionsMap,
} from '@shared/modules/map/constants/optionsMap';
import { MapService } from '@shared/modules/map/map.service';

@Component({
  selector: 'app-lab-pas-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  providers: [MapService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapComponent implements OnDestroy {
  constructor(
    private repository: LabPassRepositoryService,
    private mapService: MapService
  ) {}

  stateMap: ymaps.IMapState = {
    behaviors: ['default', 'scrollZoom'],
    controls: [],
  };
  optionsMap = optionsMap;

  placemarkOptions = placemarkCenterOptions;

  clustererOptions: ymaps.IClustererOptions = {};

  zoomControlOptions: Record<string, unknown> = {
    size: 'small',
    float: 'none',
    position: {
      bottom: '80px',
      right: '15px',
    },
  };

  geolocationControlOptions: Record<string, unknown> = {
    float: 'none',
    position: {
      top: '15px',
      right: '15px',
    },
  };

  placemarks$ = new BehaviorSubject<IPlacemark[]>([]);

  selectedLabomat: IPlacemark | null = null;
  isOpenBalloonMobile = false;

  ngUnsubscribe$ = new Subject<void>();

  onMapReady(yaMap: YaReadyEvent<ymaps.Map>): void {
    this.clustererOptions = {
      ...clustererOptions,
      clusterIconContentLayout:
        ymaps.templateLayoutFactory.createClass(iconContentLayout),
    };

    this.repository.labomates$
      .pipe(
        filter((labomates): labomates is ILabomatesResponse[] => !!labomates),
        tap((labomates) => {
          if (yaMap) {
            const yaMapTarget = yaMap.target;

            yaMapTarget.controls.add('zoomControl', this.zoomControlOptions);
            yaMapTarget.controls.add(
              'geolocationControl',
              this.geolocationControlOptions
            );
          }

          this.placemarks$.next(this.generatePlacemarks(labomates));
        }),
        switchMap(() => this.placemarks$),
        tap((placemarks) => {
          yaMap.target.setCenter(
            this.mapService.getCenterMap(
              placemarks,
              (placemark) => placemark.point
            )
          );
        }),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe();
  }

  private generatePlacemarks(labomates: ILabomatesResponse[]): IPlacemark[] {
    return labomates
      .filter((labomat) => labomat.latitude !== 0 && labomat.longitude !== 0)
      .map((labomat) => {
        return {
          point: [labomat.latitude ?? 0, labomat.longitude ?? 0],
          options: this.placemarkOptions,
          labomatInfo: labomat,
          selected: false,
        } as IPlacemark;
      });
  }

  selectPlacemark(labomat: IPlacemark): void {
    this.selectedLabomat = labomat;

    this.resetOptionsPlacemark();

    const selectedPlacemark = this.placemarks$.value.find(
      (placemark) => placemark.labomatInfo.id === labomat.labomatInfo.id
    );

    const balloonContent = this.mapService.isMobile
      ? ''
      : `<div style="font-family: Golos;" class="p-8 max-w-288">
            <div class="typography typography-headline">Лабомат</div>
            <div class="typography grey-800 mt-8">${labomat.labomatInfo.address}</div>
          </div>`;

    if (selectedPlacemark) {
      selectedPlacemark.properties = { balloonContent };

      if (this.mapService.isMobile) {
        selectedPlacemark.options = placemarkCenterSelectedOptions;
        selectedPlacemark.selected = true;

        this.isOpenBalloonMobile = true;
      }
    }
  }

  resetOptionsPlacemark(): void {
    if (this.selectedLabomat) {
      const selectedPlacemark = this.placemarks$.value.find(
        (placemark) => placemark.selected
      );

      if (selectedPlacemark) {
        selectedPlacemark.options = placemarkCenterOptions;
        selectedPlacemark.selected = false;
      }
    }
  }

  closeBalloonMobile(): void {
    this.isOpenBalloonMobile = false;

    this.resetOptionsPlacemark();
  }

  closeBalloon(yaMap: YaEvent<ymaps.Map>): void {
    if (yaMap.target.balloon.isOpen()) {
      yaMap.target.balloon.close();
    }

    if (this.isOpenBalloonMobile) this.isOpenBalloonMobile = false;

    if (this.selectedLabomat) this.resetOptionsPlacemark();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
